import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import pt from 'prop-types';
import { isEqual } from 'lodash';

const ResyncIntentContext = createContext(null);

export default function ResyncIntentProvider({ initialIdentitySyncs, children }) {
  const [enableMap, setEnableMap] = useState(() => buildEnableMap(initialIdentitySyncs));
  const initialRef = useRef(initialIdentitySyncs);

  // isEqual avoids spurious re-renders when Apollo hands us a new array reference with identical contents.
  useEffect(() => {
    if (initialRef.current !== initialIdentitySyncs) {
      initialRef.current = initialIdentitySyncs;
      setEnableMap(prev => {
        const next = buildEnableMap(initialIdentitySyncs);
        return isEqual(prev, next) ? prev : next;
      });
    }
  }, [initialIdentitySyncs]);

  const getEnableResync = useCallback(syncName => enableMap[syncName] === true, [enableMap]);

  const setEnableResync = useCallback((syncName, value) => {
    setEnableMap(prev => ({ ...prev, [syncName]: value === true }));
  }, []);

  const getAllEnableResyncs = useCallback(() => ({ ...enableMap }), [enableMap]);

  const value = useMemo(
    () => ({ getEnableResync, setEnableResync, getAllEnableResyncs }),
    [getEnableResync, setEnableResync, getAllEnableResyncs]
  );

  return <ResyncIntentContext.Provider value={value}>{children}</ResyncIntentContext.Provider>;
}

ResyncIntentProvider.propTypes = {
  initialIdentitySyncs: pt.array,
  children: pt.node,
};

export function useResyncIntent() {
  const ctx = useContext(ResyncIntentContext);
  if (!ctx) {
    throw new Error('useResyncIntent must be used within a ResyncIntentProvider');
  }
  return ctx;
}

function buildEnableMap(identitySyncs) {
  const map = {};
  (identitySyncs ?? []).forEach(row => {
    if (row.name) {
      map[row.name] = row.enableResyncOnSettingsChange === true;
    }
  });
  return map;
}
